import React, { useEffect, useState } from 'react';
import V2Icon from '../icon/v2_Icon';
import './v2_GenericDrawer.css';

/**
 * Right-anchored slide-over drawer with backdrop (v2 shell).
 *
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {Function} props.onClose
 * @param {string|React.ReactNode} [props.title] - string → h2; node → custom header-main content
 * @param {string} [props.ariaLabel] - dialog label when title is not a string
 * @param {React.ReactNode} [props.headerActions] - e.g. primary upload button (right of title area)
 * @param {React.ReactNode} props.children
 * @param {string} [props.className]
 * @param {string} [props.panelClassName]
 * @param {boolean} [props.closeOnBackdrop]
 * @param {boolean} [props.closeOnEscape]
 */
function V2GenericDrawer({
  isOpen = false,
  onClose,
  title = '',
  ariaLabel = '',
  headerActions = null,
  children = null,
  className = '',
  panelClassName = '',
  closeOnBackdrop = true,
  closeOnEscape = true,
}) {
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setEntered(false);
      return undefined;
    }

    const id = requestAnimationFrame(() => {
      setEntered(true);
    });
    return () => {
      cancelAnimationFrame(id);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !closeOnEscape) {
      return undefined;
    }

    const handleEscape = (event) => {
      if (event.key === 'Escape' && onClose) {
        onClose();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [closeOnEscape, isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const handleBackdropMouseDown = (event) => {
    if (!closeOnBackdrop || event.target !== event.currentTarget) {
      return;
    }
    if (onClose) {
      onClose();
    }
  };

  const panelClasses = [
    'v2-generic-drawer__panel',
    entered ? 'v2-generic-drawer__panel--entered' : '',
    panelClassName,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={`v2-generic-drawer__backdrop ${className}`.trim()}
      onMouseDown={handleBackdropMouseDown}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={(typeof title === 'string' && title) || ariaLabel || undefined}
        className={panelClasses}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="v2-generic-drawer__header">
          <div className="v2-generic-drawer__header-main">
            {typeof title === 'string' || typeof title === 'number'
              ? (title ? <h2 className="v2-generic-drawer__title">{title}</h2> : null)
              : title}
          </div>
          <div className="v2-generic-drawer__header-actions">
            {headerActions}
            {onClose ? (
              <button
                type="button"
                className="v2-generic-drawer__close"
                onClick={onClose}
                aria-label="Close panel"
              >
                <V2Icon name="close" />
              </button>
            ) : null}
          </div>
        </div>
        <div className="v2-generic-drawer__body">{children}</div>
      </div>
    </div>
  );
}

export default V2GenericDrawer;
